<template>
  <div class="security-document-requests">
    <h2 class="subheading grey--text">Solicitudes de Documentos</h2>
    <v-container class="my-5">
      <v-layout align-center justify-center fill-height>
        <v-btn color="primary" dark @click="showCreateDialog = true">
          + Crear Solicitud de Documento
        </v-btn>
      </v-layout>

      <v-layout class="my-4" align-center justify-center fill-height>
        <v-flex>
          <v-card-title>
            Solicitudes Creadas
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Buscar"
              single-line
              hide-details
              class="mr-3"
            ></v-text-field>
          </v-card-title>

          <v-alert v-if="securityError" type="error" dismissible class="mb-3">
            {{ securityError }}
          </v-alert>

          <v-data-table
            :headers="requestHeaders"
            :items="documentRequests"
            :search="search"
            :loading="isLoading"
            :rows-per-page-text="$t('filas_por_pagina')"
            :rows-per-page-items="[
              {text: '5', value: 5},
              {text: '10', value: 10},
              {text: '25', value: 25},
              {text: this.$t('todos'), value: -1}
            ]"
            class="elevation-1"
          >
            <template slot="items" slot-scope="props">
              <td class="justify-center layout px-0">
                <v-icon medium class="mr-2" color="primary" @click="viewDetail(props.item)">visibility</v-icon>
                <v-icon medium color="red" @click="openDeleteDialog(props.item)">delete</v-icon>
              </td>
              <td>{{ props.item.document_type_display || props.item.document_type }}</td>
              <td>{{ props.item.status_display || props.item.status }}</td>
              <td>{{ formatDate(props.item.deadline) }}</td>
              <td>{{ formatDate(props.item.created_at) }}</td>
              <td>
                <v-chip
                  :color="getStatusColor(props.item.status)"
                  text-color="white"
                  small
                >
                  {{ props.item.status_display || props.item.status }}
                </v-chip>
              </td>
            </template>
            <template v-slot:no-results>
              <v-alert :value="true" color="error" icon="warning">
                {{$t('ningun_resultado_1')}} "{{ search }}" {{$t('ningun_resultado_2')}}
              </v-alert>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- DIALOG: Crear Solicitud -->
    <v-dialog v-model="showCreateDialog" max-width="600px" persistent>
      <v-form ref="createForm" @submit.prevent="createRequest()">
        <v-card class="elevation-5">
          <v-toolbar dark color="primary">
            <v-toolbar-title>Crear Solicitud de Documento</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon color="white" flat @click="closeCreateDialog" class="align-self-end">
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pt-4">
            <v-layout row wrap>
              <v-flex xs12>
                <v-select
                  v-model="newRequest.documentType"
                  :items="documentTypes"
                  label="Tipo de Documento *"
                  required
                  @input="$refs.createForm.validate()"
                ></v-select>
              </v-flex>

              <v-flex xs12>
                <v-textarea
                  v-model="newRequest.description"
                  label="Descripción / Instrucciones"
                  rows="4"
                  placeholder="Ej: Presentar SUA actualizado antes del 15 de agosto"
                ></v-textarea>
              </v-flex>

              <v-flex xs12>
                <v-menu
                  ref="deadlineMenu"
                  :close-on-content-click="false"
                  v-model="showDeadlinePicker"
                  offset-y
                  full-width
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="newRequest.deadline"
                      label="Fecha Límite (Opcional)"
                      hint="El plazo vence a las 23:59 de este día"
                      persistent-hint
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="newRequest.deadline"
                    @input="showDeadlinePicker = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
            </v-layout>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn flat @click="closeCreateDialog">Cancelar</v-btn>
            <v-btn color="primary" type="submit" :loading="isLoading">Crear</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- DIALOG: Confirmar Eliminar -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="red--text">Confirmar Eliminación</v-card-title>
        <v-card-text>
          ¿Está seguro de que desea eliminar esta solicitud de documento?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="showDeleteDialog = false">Cancelar</v-btn>
          <v-btn color="red" @click="confirmDelete" :loading="isLoading">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  FETCH_DOCUMENT_REQUESTS,
  CREATE_DOCUMENT_REQUEST
} from '@/store/modules/security'
import { getStatusColor } from '@/utils/documentRequestHelpers'

export default {
  name: 'security_document_requests',
  computed: {
    ...mapGetters('security', {
      isLoading: 'isSecurityLoading',
      securityError: 'getSecurityError',
      documentRequests: 'getDocumentRequests'
    })
  },
  data() {
    return {
      search: '',
      showCreateDialog: false,
      showDeleteDialog: false,
      showDeadlinePicker: false,
      selectedItem: null,
      newRequest: {
        documentType: '',
        description: '',
        deadline: ''
      },
      documentTypes: [
        { text: 'SUA', value: 'SUA' },
        { text: 'INE Frente', value: 'INE_FRENTE' },
        { text: 'INE Atrás', value: 'INE_ATRAS' },
        { text: 'Responsabilidad', value: 'RESPONSABILIDAD' },
        { text: 'Licencia', value: 'LICENCIA' },
        { text: 'Otro', value: 'OTRO' }
      ],
      requestHeaders: [
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' },
        { text: 'Tipo de Documento', value: 'document_type' },
        { text: 'Estado', value: 'status' },
        { text: 'Fecha Límite', value: 'deadline' },
        { text: 'Creado', value: 'created_at' },
        { text: 'Status', value: 'status_display' }
      ]
    }
  },
  mounted() {
    this.fetchRequests()
  },
  methods: {
    ...mapActions('security', [
      FETCH_DOCUMENT_REQUESTS,
      CREATE_DOCUMENT_REQUEST
    ]),
    fetchRequests() {
      const token = this.$store.getters.getToken
      this[FETCH_DOCUMENT_REQUESTS](token)
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || 'Error al cargar solicitudes')
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    createRequest() {
      if (!this.newRequest.documentType) {
        this.$store.commit('snackbar_change_message', 'Por favor seleccione un tipo de documento')
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
        return
      }

      const token = this.$store.getters.getToken
      this[CREATE_DOCUMENT_REQUEST]({
        token,
        documentType: this.newRequest.documentType,
        description: this.newRequest.description,
        deadline: this.formatDeadlineForApi(this.newRequest.deadline)
      })
        .then(() => {
          this.$store.commit('snackbar_change_message', 'Solicitud creada correctamente')
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit('snackbar_activate_true')
          this.closeCreateDialog()
          this.fetchRequests()
        })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || 'Error al crear solicitud')
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    viewDetail(item) {
      this.$router.push({
        name: 'security_document_request_detail',
        params: { requestId: item.id }
      })
    },
    openDeleteDialog(item) {
      this.selectedItem = item
      this.showDeleteDialog = true
    },
    confirmDelete() {
      // TODO: Implementar delete action cuando esté disponible en el backend
      alert('Funcionalidad de eliminar próximamente')
      this.showDeleteDialog = false
    },
    closeCreateDialog() {
      this.showCreateDialog = false
      this.$refs.createForm.reset()
      this.newRequest = {
        documentType: '',
        description: '',
        deadline: ''
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      try {
        const date = new Date(dateStr)
        return date.toLocaleDateString('es-MX')
      } catch (e) {
        return dateStr
      }
    },
    getStatusColor,
    formatDeadlineForApi(dateStr) {
      if (!dateStr) return null
      const pad = n => String(n).padStart(2, '0')
      const date = new Date(`${dateStr}T23:59:59`)

      const offsetMinutes = -date.getTimezoneOffset()
      const offsetSign = offsetMinutes >= 0 ? '+' : '-'
      const absOffset = Math.abs(offsetMinutes)
      const offsetHours = pad(Math.floor(absOffset / 60))
      const offsetMins = pad(absOffset % 60)

      return `${dateStr}T23:59:59${offsetSign}${offsetHours}:${offsetMins}`
    }
  }
}
</script>

<style scoped>
.security-document-requests {
  padding: 20px;
}

.subheading {
  font-size: 24px;
  font-weight: 300;
}
</style>
